import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Residents from './pages/Residents';
import Diary from './pages/Diary';
import Incidents from './pages/Incidents';
import HouseholdTasks from './pages/HouseholdTasks';
import Staff from './pages/Staff';
import MedicalRecords from './pages/MedicalRecords';
import DailyCare from './pages/DailyCare';
import DailyCareLogs from './pages/DailyCareLogs';
import DailyCareLogDetail from './pages/DailyCareLogDetail';
import DailyCareCompliance from './pages/DailyCareCompliance';
import DailyCareCategoriesAdmin from './pages/DailyCareCategoriesAdmin';
import ResidentCarePlanEditor from './pages/ResidentCarePlanEditor';
import Finance from './pages/Finance';
import SupportPlans from './pages/SupportPlans';
import Assessments from './pages/Assessments';
import Training from './pages/Training';
import CarePlans from './pages/CarePlans';

const AppRoutes: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <Login />}
      />
      <Route
        path="/signup"
        element={isAuthenticated ? <Navigate to="/" replace /> : <Signup />}
      />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/residents"
        element={
          <ProtectedRoute requiredRole="admin">
            <Layout>
              <Residents />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/residents/:residentId/care-plan"
        element={
          <ProtectedRoute requiredRole="admin">
            <Layout>
              <ResidentCarePlanEditor />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/diary"
        element={
          <ProtectedRoute>
            <Layout>
              <Diary />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/diary/:residentId"
        element={
          <ProtectedRoute>
            <Layout>
              <Diary />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/daily-care"
        element={
          <ProtectedRoute>
            <Layout>
              <DailyCare />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/daily-care-logs"
        element={
          <ProtectedRoute requiredRole="admin">
            <Layout>
              <DailyCareLogs />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/daily-care-logs/compliance"
        element={
          <ProtectedRoute requiredRole="admin">
            <Layout>
              <DailyCareCompliance />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/daily-care-logs/categories"
        element={
          <ProtectedRoute requiredRole="admin">
            <Layout>
              <DailyCareCategoriesAdmin />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/daily-care-logs/:logId"
        element={
          <ProtectedRoute requiredRole="admin">
            <Layout>
              <DailyCareLogDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/incidents"
        element={
          <ProtectedRoute>
            <Layout>
              <Incidents />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/household-tasks"
        element={
          <ProtectedRoute>
            <Layout>
              <HouseholdTasks />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/support-plans"
        element={
          <ProtectedRoute>
            <Layout>
              <SupportPlans />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/appointments"
        element={
          <ProtectedRoute>
            <Layout>
              <div className="text-center py-12">
                <h2 className="text-2xl font-bold text-gray-900">Appointments</h2>
                <p className="mt-2 text-gray-600">Appointments management coming soon...</p>
              </div>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff"
        element={
          <ProtectedRoute requiredRole="admin">
            <Layout>
              <Staff />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/medical-records"
        element={
          <ProtectedRoute requiredRole="admin">
            <Layout>
              <MedicalRecords />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/finance"
        element={
          <ProtectedRoute requiredRole="admin">
            <Layout>
              <Finance />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/assessments"
        element={
          <ProtectedRoute>
            <Layout>
              <Assessments />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/training"
        element={
          <ProtectedRoute>
            <Layout>
              <Training />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/care-plans"
        element={
          <ProtectedRoute>
            <Layout>
              <CarePlans />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;
